import { z } from 'zod';

import {
  COMMAND_STATUS,
  COMMAND_TYPE,
  DEVICE_STATUS,
  SUBSCRIPTION_STATUS,
  type CommandStatus,
  type CommandType,
  type DeviceStatus,
} from './constants';
import type { Device } from './schemas';

// Device Details payloads.
//
// The details payload itself is CONFIRMED (probed live 2026-08-13); `location`, `queuedCommand`,
// the stop-timer responses and the flow-rate response are **not** — the OpenAPI spec publishes no
// response schema for this tag at all. See
// greenway_mobile/docs/specs/features/devices/device-details/api-contract.md.
//
// So the confirmed parts are described with zod, and the unconfirmed parts with hand-written
// tolerant readers that accept every plausible spelling and **degrade to an empty/absent state
// rather than throwing**. A mismatch must render one card's own empty state, not take the whole
// page down.

// ── small tolerant readers ────────────────────────────────────────────────────────────────────

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** First present key, or undefined. */
function pick(source: Record<string, unknown>, ...keys: string[]): unknown {
  for (const key of keys) {
    if (source[key] !== undefined && source[key] !== null) return source[key];
  }
  return undefined;
}

function readNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

function readString(value: unknown): string | null {
  if (typeof value === 'string' && value.trim() !== '') return value;
  if (typeof value === 'number') return String(value);
  return null;
}

/** ISO instant, or epoch millis, or nothing. */
function readInstant(value: unknown): string | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return new Date(value).toISOString();
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const ms = Date.parse(value);
    if (!Number.isNaN(ms)) return new Date(ms).toISOString();
  }
  return null;
}

function readCommandType(value: unknown): CommandType | null {
  if (typeof value !== 'string') return null;
  switch (value) {
    case 'turn_on':
    case 'TURN_ON':
    case 'turnOn':
      return COMMAND_TYPE.TURN_ON;
    case 'turn_off':
    case 'TURN_OFF':
    case 'turnOff':
      return COMMAND_TYPE.TURN_OFF;
    default:
      return null;
  }
}

// ── header subscription indicator ─────────────────────────────────────────────────────────────

/**
 * The badge beside the device name (US-112437 §1): a shield while the subscription is comfortably
 * active, an info icon when it expires within 30 days, and nothing when the backend sends
 * something this app doesn't recognise.
 */
export const SUBSCRIPTION_INDICATOR = {
  PROTECTED: 'protected',
  EXPIRING_SOON: 'expiring_soon',
  EXPIRED: 'expired',
  NONE: 'none',
} as const;

export type SubscriptionIndicator =
  (typeof SUBSCRIPTION_INDICATOR)[keyof typeof SUBSCRIPTION_INDICATOR];

function readSubscriptionIndicator(value: unknown): SubscriptionIndicator {
  switch (value) {
    case 'protected':
      return SUBSCRIPTION_INDICATOR.PROTECTED;
    case 'expiring_soon':
    case 'expiringSoon':
      return SUBSCRIPTION_INDICATOR.EXPIRING_SOON;
    case 'expired':
      return SUBSCRIPTION_INDICATOR.EXPIRED;
    default:
      return SUBSCRIPTION_INDICATOR.NONE;
  }
}

// ── details payload (CONFIRMED) ───────────────────────────────────────────────────────────────

const nullableNumber = z.number().nullish().catch(null);

/** All three readings are nullable: a device that has never reported returns three nulls. */
export const deviceMetricsWireSchema = z
  .object({
    batteryVoltage: nullableNumber,
    temperatureF: nullableNumber,
    waterLevelInches: nullableNumber,
  })
  .catch({ batteryVoltage: null, temperatureF: null, waterLevelInches: null });

export const deviceHardwareWireSchema = z
  .object({
    category: z.string().nullish().catch(null),
    serialNumber: z.string().nullish().catch(null),
    /** Free-form on the wire ("active" observed); anything else reads as expired. */
    warrantyStatus: z.string().nullish().catch(null),
    warrantyValidTill: z.string().nullish().catch(null),
    subscriptionPlan: z.string().nullish().catch(null),
    subscriptionStatus: z
      .enum([SUBSCRIPTION_STATUS.ACTIVE, SUBSCRIPTION_STATUS.EXPIRED])
      .catch(SUBSCRIPTION_STATUS.ACTIVE),
  })
  .catch({
    category: null,
    serialNumber: null,
    warrantyStatus: null,
    warrantyValidTill: null,
    subscriptionPlan: null,
    subscriptionStatus: SUBSCRIPTION_STATUS.ACTIVE,
  });

const controlStateSchema = z
  .object({
    available: z.boolean().catch(false),
    enabled: z.boolean().catch(false),
  })
  .catch({ available: false, enabled: false });

// ── normalised domain types ───────────────────────────────────────────────────────────────────

export type DeviceMetrics = {
  batteryVoltage: number | null;
  temperatureF: number | null;
  waterLevelInches: number | null;
};

export type DeviceHardware = {
  category: string | null;
  serialNumber: string | null;
  warrantyStatus: string | null;
  warrantyValidTill: string | null;
  subscriptionPlan: string | null;
  subscriptionStatus: Device['subscriptionStatus'];
  /** `warrantyStatus` is free-form; anything that isn't "active" renders as Expired. */
  warrantyExpired: boolean;
};

export type StopTimer = {
  /** Whether the Stop Timer pill is usable at all — a `controls`-level fact. */
  enabled: boolean;
  active: boolean;
  stopsAt: string | null;
  startedAt: string | null;
  durationMinutes: number | null;
};

export const EMPTY_STOP_TIMER: StopTimer = {
  enabled: false,
  active: false,
  stopsAt: null,
  startedAt: null,
  durationMinutes: null,
};

export type DeviceControls = {
  turnOn: { available: boolean; enabled: boolean };
  turnOff: { available: boolean; enabled: boolean };
  stopTimer: StopTimer;
  scheduleConfigured: boolean;
};

export type DeviceLocation = {
  fieldId: string | null;
  fieldName: string | null;
  city: string | null;
  state: string | null;
  acres: number | null;
  cropName: string | null;
  cropYear: string | null;
  lat: number | null;
  lng: number | null;
  /** Boundary rings, when the device sits inside a field. */
  boundary: { lat: number; lng: number }[][];
};

export type DetailsQueuedCommand = {
  id: string | null;
  type: CommandType;
  queuedAt: string | null;
  /** Where the command sits in the lifecycle. Absent on the wire ⇒ still queued. */
  status: CommandStatus;
};

export type DeviceDetails = {
  id: string;
  defaultName: string;
  customName: string | null;
  /** `effectiveStatus` with an expired subscription folded in, as the listing does. */
  status: DeviceStatus;
  /** The raw live status, before the expiry fold — the metrics dot keys off this. */
  effectiveStatus: DeviceStatus;
  subscriptionIndicator: SubscriptionIndicator;
  subscriptionDaysRemaining: number | null;
  metrics: DeviceMetrics;
  lastSyncedAt: string | null;
  location: DeviceLocation | null;
  hardware: DeviceHardware;
  controls: DeviceControls;
  /**
   * The one command awaiting execution, if any — the banner and the disabled power disc both
   * key off this. A command that has already **failed** is deliberately *not* here: §9 requires
   * the queued command to be cleared and the control returned to its pre-queue state.
   */
  queuedCommand: DetailsQueuedCommand | null;
  /** A command that came back Failed, so the page can announce it once (§9). */
  failedCommand: DetailsQueuedCommand | null;
  expired: boolean;
};

// ── parsing ───────────────────────────────────────────────────────────────────────────────────

/** UNCONFIRMED shape — every probed device returned `location: null`. Tolerant on purpose. */
function readLocation(value: unknown): DeviceLocation | null {
  if (!isRecord(value)) return null;
  const field = isRecord(value.field) ? value.field : {};

  return {
    fieldId: readString(pick(field, 'id') ?? pick(value, 'fieldId')),
    fieldName: readString(pick(field, 'name') ?? pick(value, 'fieldName')),
    city: readString(pick(value, 'city')),
    state: readString(pick(value, 'state', 'stateName')),
    acres: readNumber(pick(field, 'acres') ?? pick(value, 'acres')),
    cropName: readString(pick(field, 'cropName') ?? pick(value, 'cropName', 'crop')),
    cropYear: readString(pick(field, 'cropYear') ?? pick(value, 'cropYear')),
    lat: readNumber(pick(value, 'lat', 'latitude')),
    lng: readNumber(pick(value, 'lng', 'lon', 'longitude')),
    boundary: readBoundary(pick(field, 'boundary') ?? pick(value, 'boundary')),
  };
}

/** Accepts a ring or an array of rings, with `lat`/`lng` under either spelling. */
function readBoundary(value: unknown): { lat: number; lng: number }[][] {
  if (!Array.isArray(value) || value.length === 0) return [];
  const asPoint = (entry: unknown) => {
    if (!isRecord(entry)) return null;
    const lat = readNumber(pick(entry, 'lat', 'latitude'));
    const lng = readNumber(pick(entry, 'lng', 'lon', 'longitude'));
    return lat != null && lng != null ? { lat, lng } : null;
  };

  // A bare ring (array of points) vs an array of rings.
  const rings = Array.isArray(value[0]) ? (value as unknown[][]) : [value as unknown[]];
  return rings
    .map((ring) =>
      ring.map(asPoint).filter((point): point is { lat: number; lng: number } => !!point),
    )
    .filter((ring) => ring.length >= 3);
}

/** Returns null for anything unrecognisable, so an unknown shape simply hides the banner. */
export function readQueuedCommand(value: unknown): DetailsQueuedCommand | null {
  if (value == null) return null;
  if (typeof value === 'string') {
    const type = readCommandType(value);
    return type ? { id: null, type, queuedAt: null, status: COMMAND_STATUS.QUEUED } : null;
  }
  if (!isRecord(value)) return null;

  const type = readCommandType(pick(value, 'type', 'action', 'command'));
  if (!type) return null;
  return {
    id: readString(pick(value, 'id', 'commandId')),
    type,
    queuedAt: readInstant(pick(value, 'queuedAt', 'createdAt')),
    status: readCommandStatus(pick(value, 'status', 'state')),
  };
}

/**
 * The command's lifecycle state. UNCONFIRMED on the wire — every probed device returned a bare
 * queued command with no status — so anything unrecognised is read as still *queued*, which is
 * the state that keeps the banner up and the control disabled. Only an explicit failure clears
 * them (§9).
 */
function readCommandStatus(value: unknown): CommandStatus {
  if (typeof value !== 'string') return COMMAND_STATUS.QUEUED;
  switch (value.toLowerCase()) {
    case 'failed':
    case 'failure':
    case 'error':
      return COMMAND_STATUS.FAILED;
    case 'executed':
    case 'completed':
    case 'success':
      return COMMAND_STATUS.EXECUTED;
    case 'delivered':
    case 'sent':
      return COMMAND_STATUS.DELIVERED;
    default:
      return COMMAND_STATUS.QUEUED;
  }
}

function readStopTimerControl(value: unknown): StopTimer {
  if (!isRecord(value)) return EMPTY_STOP_TIMER;
  return {
    enabled: value.enabled === true,
    active: value.active === true,
    stopsAt: readInstant(pick(value, 'stopsAt')),
    startedAt: readInstant(pick(value, 'startedAt')),
    durationMinutes: readNumber(pick(value, 'durationMinutes')),
  };
}

/**
 * The stop-timer **endpoint's** payload, which describes *a timer* rather than the details page's
 * *control*. Two deliberate differences from {@link readStopTimerControl}:
 *
 * - `enabled` is not carried over — it is a control-level fact these endpoints have no reason to
 *   report, so the caller keeps whatever `controls.stopTimer` already said.
 * - `active` is **inferred from a future `stopsAt`** when the key is absent: the GET is documented
 *   as returning *the active* timer, so a returned timer that hasn't elapsed is running whether or
 *   not it says so. An explicit `active: false` still wins.
 */
export function readStopTimerPayload(value: unknown, now: Date = new Date()): StopTimer {
  if (!isRecord(value)) return EMPTY_STOP_TIMER;
  // Some backends nest the timer; unwrap first.
  const body = isRecord(value.stopTimer)
    ? value.stopTimer
    : isRecord(value.timer)
      ? value.timer
      : value;

  const stopsAt = readInstant(pick(body, 'stopsAt', 'stopAt', 'endsAt'));
  const durationMinutes = readNumber(pick(body, 'durationMinutes', 'duration'));
  if (stopsAt == null && durationMinutes == null && body.active === undefined) {
    return EMPTY_STOP_TIMER;
  }

  return {
    enabled: body.enabled === true,
    active:
      typeof body.active === 'boolean'
        ? body.active
        : stopsAt != null && Date.parse(stopsAt) > now.getTime(),
    stopsAt,
    startedAt: readInstant(pick(body, 'startedAt', 'createdAt')),
    durationMinutes,
  };
}

const effectiveStatusSchema = z
  .enum([
    DEVICE_STATUS.RUNNING,
    DEVICE_STATUS.OFF,
    DEVICE_STATUS.DISCONNECTED,
    DEVICE_STATUS.PENDING_QC,
    DEVICE_STATUS.PENDING_CONFIGURATION,
  ])
  .catch(DEVICE_STATUS.PENDING_CONFIGURATION);

/** The confirmed skeleton of the details payload; the loose blocks are read separately. */
export const deviceDetailsWireSchema = z.object({
  id: z.string().catch(''),
  defaultName: z.string().nullish().catch(null),
  customName: z.string().nullish().catch(null),
  effectiveStatus: effectiveStatusSchema,
  // `z.unknown()` is *required* in zod 4, so these loose blocks must be marked optional or
  // an empty payload throws — which would defeat the whole point of parsing tolerantly.
  subscriptionIndicator: z.unknown().optional(),
  subscriptionDaysRemaining: nullableNumber,
  metrics: deviceMetricsWireSchema,
  lastSyncedAt: z.string().nullish().catch(null),
  location: z.unknown().optional(),
  deviceDetails: deviceHardwareWireSchema,
  controls: z.unknown().optional(),
  queuedCommand: z.unknown().optional(),
});

export function toDeviceDetails(raw: unknown): DeviceDetails {
  const wire = deviceDetailsWireSchema.parse(isRecord(raw) ? raw : {});
  const controlsRaw = isRecord(wire.controls) ? wire.controls : {};
  const hardware = wire.deviceDetails;

  const indicator = readSubscriptionIndicator(wire.subscriptionIndicator);
  const expired =
    hardware.subscriptionStatus === SUBSCRIPTION_STATUS.EXPIRED ||
    indicator === SUBSCRIPTION_INDICATOR.EXPIRED;

  const warrantyStatus = hardware.warrantyStatus ?? null;

  return {
    id: wire.id,
    defaultName: wire.defaultName ?? '',
    customName: wire.customName ?? null,
    effectiveStatus: wire.effectiveStatus,
    status: expired ? DEVICE_STATUS.EXPIRED : wire.effectiveStatus,
    subscriptionIndicator: indicator,
    subscriptionDaysRemaining: wire.subscriptionDaysRemaining ?? null,
    metrics: {
      batteryVoltage: wire.metrics.batteryVoltage ?? null,
      temperatureF: wire.metrics.temperatureF ?? null,
      waterLevelInches: wire.metrics.waterLevelInches ?? null,
    },
    lastSyncedAt: wire.lastSyncedAt ?? null,
    location: readLocation(wire.location),
    hardware: {
      category: hardware.category ?? null,
      serialNumber: hardware.serialNumber ?? null,
      warrantyStatus,
      warrantyValidTill: hardware.warrantyValidTill ?? null,
      subscriptionPlan: hardware.subscriptionPlan ?? null,
      subscriptionStatus: hardware.subscriptionStatus,
      warrantyExpired: warrantyStatus != null && warrantyStatus.toLowerCase() !== 'active',
    },
    controls: {
      turnOn: controlStateSchema.parse(controlsRaw.turnOn ?? {}),
      turnOff: controlStateSchema.parse(controlsRaw.turnOff ?? {}),
      stopTimer: readStopTimerControl(controlsRaw.stopTimer),
      scheduleConfigured: controlsRaw.scheduleConfigured === true,
    },
    ...splitCommand(readQueuedCommand(wire.queuedCommand)),
    expired,
  };
}

/**
 * Route one wire command into the field that describes it (§9).
 *
 * A failed command must not keep the banner up or the disc greyed — the AC says it is *cleared*
 * and the control returns to its pre-queue state — so it lands in `failedCommand` instead, where
 * the page turns it into the "Command couldn't be delivered" message. An executed command is
 * neither: it is simply over, and the refreshed status already tells that story.
 */
function splitCommand(command: DetailsQueuedCommand | null): {
  queuedCommand: DetailsQueuedCommand | null;
  failedCommand: DetailsQueuedCommand | null;
} {
  if (!command) return { queuedCommand: null, failedCommand: null };
  if (command.status === COMMAND_STATUS.FAILED) {
    return { queuedCommand: null, failedCommand: command };
  }
  if (command.status === COMMAND_STATUS.EXECUTED) {
    return { queuedCommand: null, failedCommand: null };
  }
  return { queuedCommand: command, failedCommand: null };
}

/**
 * Adapt a details payload to the listing's `Device` shape, so the status chip and the power rules
 * stay in one place instead of being reimplemented for this page.
 *
 * `controls.turnOff.available` is the server's "this pump is running" signal, exactly as
 * `powerIcon.action === turn_off` is on a list row.
 */
export function detailsToDevice(details: DeviceDetails): Device {
  const action = details.controls.turnOff.available
    ? COMMAND_TYPE.TURN_OFF
    : details.controls.turnOn.available
      ? COMMAND_TYPE.TURN_ON
      : null;

  return {
    id: details.id,
    name: details.defaultName,
    customName: details.customName,
    category: details.hardware.category ?? '',
    status: details.status,
    lastSyncedAt: details.lastSyncedAt,
    subscriptionStatus: details.hardware.subscriptionStatus,
    subscriptionExpiresAt: null,
    fields: details.location?.fieldName
      ? [{ id: details.location.fieldId, name: details.location.fieldName }]
      : [],
    queuedCommand: details.queuedCommand
      ? { type: details.queuedCommand.type, commandId: details.queuedCommand.id }
      : null,
    powerIcon: {
      visible: details.controls.turnOn.available || details.controls.turnOff.available,
      enabled:
        (details.controls.turnOn.enabled || details.controls.turnOff.enabled) &&
        details.queuedCommand == null,
      action,
    },
    cardDisabled: false,
  };
}

// ── schedule (CONFIRMED) ──────────────────────────────────────────────────────────────────────

/** Wire order, Sunday first — the endpoint's own enum. Index doubles as the day number. */
export const SCHEDULE_DAY_CODES = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'] as const;

export type DeviceSchedule = {
  id: string | null;
  /** 0 = Sunday … 6 = Saturday, which is what the day chips index. */
  days: number[];
  /** `HH:mm`, zone-less. Null for diesel (stop-only) devices. */
  startTime: string | null;
  stopTime: string | null;
  repeatWeekly: boolean;
};

export const EMPTY_SCHEDULE: DeviceSchedule = {
  id: null,
  days: [],
  startTime: null,
  stopTime: null,
  repeatWeekly: false,
};

/** `"HH:mm"` (or `"HH:mm:ss"`) → normalised `"HH:mm"`, or null. */
export function readClockTime(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const parts = value.trim().split(':');
  if (parts.length < 2) return null;
  const hour = Number(parts[0]);
  const minute = Number(parts[1]);
  if (!Number.isInteger(hour) || !Number.isInteger(minute)) return null;
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null;
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

export function readSchedule(value: unknown): DeviceSchedule | null {
  // `data: null` is the documented "no schedule yet" answer — a 200, not a 404.
  if (!isRecord(value) || Object.keys(value).length === 0) return null;
  const days = Array.isArray(value.days)
    ? value.days
        .map((code) =>
          typeof code === 'string'
            ? SCHEDULE_DAY_CODES.indexOf(code.toUpperCase() as (typeof SCHEDULE_DAY_CODES)[number])
            : -1,
        )
        .filter((index) => index >= 0)
    : [];

  return {
    id: readString(pick(value, 'id')),
    days: [...new Set(days)].sort((a, b) => a - b),
    startTime: readClockTime(value.startTime),
    stopTime: readClockTime(value.stopTime),
    repeatWeekly: value.repeatWeekly === true,
  };
}

/**
 * The `PUT` body. `startTime` is **omitted entirely** (not sent as null) when absent, because the
 * endpoint rejects the wrong combination for a device type rather than ignoring the key.
 */
export function scheduleRequestBody(schedule: DeviceSchedule): Record<string, unknown> {
  const days = [...schedule.days]
    .filter((day) => day >= 0 && day <= 6)
    .sort((a, b) => a - b)
    .map((day) => SCHEDULE_DAY_CODES[day]);

  return {
    ...(schedule.startTime ? { startTime: schedule.startTime } : {}),
    stopTime: schedule.stopTime,
    days,
    repeatWeekly: schedule.repeatWeekly,
  };
}

// ── flow rate (request CONFIRMED, response UNCONFIRMED) ───────────────────────────────────────

export const FLOW_RATE_RANGE = {
  TODAY: 'today',
  SEVEN_DAYS: '7d',
  THIRTY_DAYS: '30d',
  ONE_YEAR: '1y',
  ALL_TIME: 'all',
} as const;

export type FlowRateRange = (typeof FLOW_RATE_RANGE)[keyof typeof FLOW_RATE_RANGE];

export const FLOW_RATE_RANGES: { value: FlowRateRange; label: string }[] = [
  { value: FLOW_RATE_RANGE.TODAY, label: 'Today' },
  { value: FLOW_RATE_RANGE.SEVEN_DAYS, label: '7 Days' },
  { value: FLOW_RATE_RANGE.THIRTY_DAYS, label: '30 Days' },
  { value: FLOW_RATE_RANGE.ONE_YEAR, label: '1 Year' },
  { value: FLOW_RATE_RANGE.ALL_TIME, label: 'All Time' },
];

export type FlowRatePoint = {
  /** UTC ISO instant of the bucket, or null when the server sent only a label. */
  at: string | null;
  value: number;
  /** Server-supplied axis label, if any; otherwise derived per range at render time. */
  label: string | null;
};

export type FlowRateSeries = {
  points: FlowRatePoint[];
  averageGpm: number | null;
  runtimeMinutes: number | null;
  gallonsPumped: number | null;
};

export const EMPTY_FLOW_RATE: FlowRateSeries = {
  points: [],
  averageGpm: null,
  runtimeMinutes: null,
  gallonsPumped: null,
};

/**
 * Parse a flow-rate response. **Deliberately tolerant**: the endpoint publishes no response
 * schema and no probe account was available, so this accepts the shapes the backend plausibly
 * returns and degrades to an empty series — which renders the card's "no data for this period"
 * state — rather than erroring the whole page. Re-probe and tighten.
 */
export function readFlowRate(raw: unknown): FlowRateSeries {
  const container = isRecord(raw) ? raw : {};
  const rawPoints = Array.isArray(raw)
    ? raw
    : (pick(container, 'points', 'data', 'series', 'readings', 'items') as unknown);
  const list = Array.isArray(rawPoints) ? rawPoints : [];

  const points = list
    .map((entry): FlowRatePoint | null => {
      if (!isRecord(entry)) {
        // A bare number is a value with no instant — still plottable in order.
        const bare = readNumber(entry);
        return bare == null ? null : { at: null, value: bare, label: null };
      }
      // A bucket with no recognisable value is dropped, not plotted as a spurious zero.
      const value = readNumber(
        pick(entry, 'value', 'flowRate', 'gpm', 'avgGpm', 'averageGpm', 'gallonsPerMinute'),
      );
      if (value == null) return null;
      return {
        at: readInstant(
          pick(entry, 'timestamp', 'time', 'date', 'bucket', 'bucketStart', 'periodStart', 'at'),
        ),
        value,
        label: readString(pick(entry, 'label', 'name')),
      };
    })
    .filter((point): point is FlowRatePoint => point !== null);

  const summarySource = isRecord(pick(container, 'summary', 'totals'))
    ? (pick(container, 'summary', 'totals') as Record<string, unknown>)
    : container;

  const average = readNumber(
    pick(summarySource, 'averageGpm', 'average', 'avgGpm', 'averageFlowRate'),
  );
  // Runtime arrives in whichever unit the backend fancies; normalise to minutes.
  const runtimeMinutes =
    readNumber(pick(summarySource, 'runtimeMinutes', 'totalRuntimeMinutes')) ??
    (() => {
      const seconds = readNumber(pick(summarySource, 'runtimeSeconds', 'totalRuntimeSeconds'));
      if (seconds != null) return seconds / 60;
      const hours = readNumber(pick(summarySource, 'runtimeHours', 'totalRuntimeHours'));
      return hours != null ? hours * 60 : null;
    })();

  return {
    points,
    // With no server average, the mean of the points beats an em dash next to a chart that
    // plainly has data. Runtime and gallons cannot be derived and stay null.
    averageGpm:
      average ??
      (points.length > 0
        ? points.reduce((total, point) => total + point.value, 0) / points.length
        : null),
    runtimeMinutes,
    gallonsPumped: readNumber(
      pick(summarySource, 'gallonsPumped', 'totalGallons', 'gallons', 'totalGallonsPumped'),
    ),
  };
}
